import { WarningAmber as WarningAmberIcon } from '@mui/icons-material';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { Trash2 } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { LabeledValue, LabelIdValue } from '~/components/Fields/LabeledValue';
import { Series } from '~/domain/dicom/series';
import { formatSeriesDate } from '~/utils/date';

interface RawSeriesPropertiesProps {
    series: Series;
    onDelete?: () => void;
}

export default function RawSeriesProperties({
    series,
    onDelete,
}: RawSeriesPropertiesProps) {
    const intl = useIntl();
    const sm = series.seriesModule;
    const firstSlice = series.dicomImageSlices[0];

    const spacingCm = firstSlice
        ? Array.from(firstSlice.pixelSpacing)
              .map((s) => (s / 10).toFixed(3))
              .join(' x ')
        : undefined;

    return (
        <Box display="flex" flexDirection="column" gap={0.25}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={1}
                mb={0.5}
            >
                <Box display="flex" alignItems="center" gap={1}>
                    <WarningAmberIcon
                        sx={{
                            fontSize: 14,
                            color: 'warning.main',
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        fontSize={11}
                        color="warning.main"
                        fontWeight={500}
                    >
                        <FormattedMessage id="reformatDescription" />
                    </Typography>
                </Box>
                {onDelete && (
                    <Tooltip title={intl.formatMessage({ id: 'delete' })}>
                        <IconButton
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                onDelete();
                            }}
                            sx={{ p: 0.25 }}
                            aria-label={intl.formatMessage({ id: 'delete' })}
                        >
                            <Trash2 size={14} />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
            {firstSlice?.imageType?.length > 0 && (
                <LabeledValue
                    label={<FormattedMessage id="imageType" />}
                    value={firstSlice.imageType.join(', ')}
                    inline
                />
            )}
            <LabeledValue
                label={<FormattedMessage id="seriesDescription" />}
                value={sm.seriesDescription || '-'}
                sensitive
                inline
            />
            {sm.seriesDate && (
                <LabeledValue
                    label={<FormattedMessage id="seriesDate" />}
                    value={formatSeriesDate(sm.seriesDate)}
                    sensitive
                    inline
                />
            )}
            <LabeledValue
                label={<FormattedMessage id="modality" />}
                value={sm.modality}
                inline
            />
            {firstSlice && (
                <LabeledValue
                    label={<FormattedMessage id="matrix" />}
                    value={`${firstSlice.cols} x ${firstSlice.rows} x ${series.numberOfObjects}`}
                    inline
                />
            )}
            <LabelIdValue
                label={intl.formatMessage({ id: 'seriesInstanceUid' })}
                id={sm.seriesInstanceUID}
                inline
            />
            <LabelIdValue
                label={intl.formatMessage({
                    id: 'frameOfReferenceUid',
                })}
                id={sm.frameOfReferenceUid}
                inline
            />
            {firstSlice?.manufacturer && (
                <LabeledValue
                    label={
                        <>
                            <FormattedMessage id="manufacturer" />:
                        </>
                    }
                    value={firstSlice.manufacturer}
                    inline
                />
            )}
            {spacingCm && (
                <LabeledValue
                    label={
                        <>
                            <FormattedMessage id="resolution" /> (cm):
                        </>
                    }
                    value={spacingCm}
                    inline
                />
            )}
        </Box>
    );
}
